import L from 'leaflet';
import { ContestTaskRenderer } from '../utils/ContestTaskRenderer.js';

/**
 * Manages multiple contest task maps in the timeline view
 * Each contest task gets its own map instance
 */
export default class ContestTaskTimelineMap {
  constructor() {
    this.maps = new Map(); // Store map instances by contest task ID
    this.renderers = new Map(); // Store renderers by contest task ID
  }

  /**
   * Initialize all contest task maps currently in the DOM
   */
  initializeMaps() {
    const mapContainers = document.querySelectorAll('.contest-task-timeline-map');

    mapContainers.forEach(container => {
      const contestTaskId = parseInt(container.dataset.contestTaskId);

      // Skip if already initialized
      if (this.maps.has(contestTaskId)) {
        return;
      }

      this.initializeMap(contestTaskId, container.id);
    });
  }

  /**
   * Initialize a single map for a contest task
   */
  initializeMap(contestTaskId, containerId) {
    try {
      // Create the map
      const map = L.map(containerId, {
        attributionControl: false,
        scrollWheelZoom: false, // Disable scroll zoom to prevent interference with page scrolling
        dragging: true,
        touchZoom: true,
        doubleClickZoom: true,
        boxZoom: true,
        keyboard: false,
        zoomControl: true
      }).setView([41.831131, 15.624207], 3);

      // Add tile layer
      L.tileLayer('https://tile.tracestrack.com/_/{z}/{x}/{y}.png?key=5b37c4cf109fa26755308c3a5e221369', {
        attribution: '&copy; SportsTrackLive'
      }).addTo(map);

      // Store the map instance
      this.maps.set(contestTaskId, map);

      // Initialize renderer for this map
      const renderer = new ContestTaskRenderer(map);
      this.renderers.set(contestTaskId, renderer);

      // Load and render the contest task
      renderer.renderFromAPI(contestTaskId, () => {
        // Callback after rendering is complete
        // Map bounds are already fit by the renderer
      });

      // Enable scroll wheel zoom when user clicks on the map
      map.once('click', () => {
        map.scrollWheelZoom.enable();
      });

      // Disable scroll wheel zoom when mouse leaves the map
      map.on('mouseout', () => {
        map.scrollWheelZoom.disable();
      });

    } catch (error) {
      console.error(`Error initializing map for contest task ${contestTaskId}:`, error);
    }
  }

  /**
   * Destroy a specific map instance
   */
  destroyMap(contestTaskId) {
    const map = this.maps.get(contestTaskId);
    if (map) {
      map.remove();
      this.maps.delete(contestTaskId);
      this.renderers.delete(contestTaskId);
    }
  }

  /**
   * Destroy all map instances
   */
  destroyAllMaps() {
    this.maps.forEach((map, contestTaskId) => {
      this.destroyMap(contestTaskId);
    });
    this.maps.clear();
    this.renderers.clear();
  }

  /**
   * Refresh maps after new timeline items are loaded
   * This should be called after AJAX loads new contest tasks
   */
  refreshMaps() {
    // Initialize any new maps that appeared in the DOM
    this.initializeMaps();
  }
}
